/**
 * Utilities for detecting and manipulating text input elements
 */

/**
 * Check if an element is a valid text input for dictation
 */
export const isTextInputElement = (
  element: Element | null,
): element is HTMLInputElement | HTMLTextAreaElement => {
  if (!element) return false;

  // Check if element is disabled or readonly
  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement
  ) {
    if (element.disabled || element.readOnly) return false;
  }

  // Check for textarea
  if (element.tagName === "TEXTAREA") return true;

  // Check for text input types
  if (element.tagName === "INPUT") {
    const input = element as HTMLInputElement;
    const validTypes = ["text", "email", "tel", "url", "search", "password"];
    return validTypes.includes(input.type);
  }

  // Check for contentEditable elements
  if (
    (element as HTMLElement).isContentEditable &&
    !element.getAttribute("contenteditable")?.toLowerCase().includes("false")
  ) {
    return true;
  }

  return false;
};

/**
 * Set an input's value so React's controlled-component tracker sees the change.
 *
 * React caches the last value it wrote on the DOM node. Assigning `.value`
 * directly updates that cache too, so the input event that follows looks like a
 * no-op and `onChange` never fires. Going through the prototype setter writes
 * the value without touching the cache, leaving the event to register normally.
 *
 * Used instead of `execCommand("insertText")` wherever text has to be *replaced*
 * rather than inserted at the cursor — streaming transcription rewrites the span
 * it wrote as the model revises it.
 */
export const setInputValue = (
  element: HTMLInputElement | HTMLTextAreaElement,
  value: string,
  source?: "dictation",
): void => {
  const prototype =
    element instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;

  if (setter) {
    setter.call(element, value);
  } else {
    element.value = value;
  }

  element.dispatchEvent(
    new CustomEvent("input", { bubbles: true, detail: { source } }),
  );
};

/**
 * Check if browser supports the required APIs for dictation
 */
export const isBrowserSupported = (): boolean => {
  return !!(
    typeof window !== "undefined" &&
    navigator.mediaDevices &&
    typeof MediaRecorder !== "undefined" &&
    typeof WebSocket !== "undefined"
  );
};

/**
 * Check microphone permission status
 */
export const checkMicrophonePermission = async (): Promise<
  "granted" | "denied" | "prompt"
> => {
  try {
    if (!navigator.permissions) return "prompt";

    const result = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    return result.state;
  } catch {
    // Fallback for browsers that don't support permissions API
    return "prompt";
  }
};

export const hasActiveTextSelection = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const selection = window.getSelection();
  if (!selection) {
    return false;
  }

  return selection.toString().trim().length > 0;
};
